import stores from "@/stores"

type StepIndex = 1 | 2 | 3 | 4

const DEFAULT_THEME_COLOR = "#1274FF"

/**
 * onboarding step_index mapping:
 *
  | 步骤序号 | 步骤名称 |
  | --- | --- |
  | 1 | Let's set up your return center |
  | 2 | We've configured this for you |
  | 3 | This is what your customers will see |
  | 4 | You're all set! |
 */
const STEP_TITLES: Record<StepIndex, string> = {
  1: "Let's set up your return center",
  2: "We've configured this for you",
  3: "This is what your customers will see",
  4: "You're all set!",
}

const CATEGORY_LABELS: Record<string, string> = {
  apparel_accessories: "Apparel & accessories",
  home_garden: "Home & garden",
  bags_luggage: "Bags & luggage",
  health_beauty: "Health & beauty",
  electronics: "Electronics",
  other: "Other",
}

const REGION_LABELS: Record<string, string> = {
  us: "United States",
  uk: "United Kingdom",
  eu: "European Union",
  au: "Australia / NZ",
  other: "Other regions",
}

const SOLUTION_LABELS: Record<number, string> = {
  5: "Exchange item",
  3: "Replace item",
  1: "Refund to original payment",
  7: "Refund to store credit",
  2: "Refund to store discount",
  4: "Refund to store gift card",
}

const RULE_TYPE_LABELS: Record<string, string> = {
  order_fulfillment: "Order fulfillment",
  order_delivery: "Order delivery",
  order_created: "Order created",
}

const report = (event: string, data?: Record<string, unknown>) => {
  console.log("report", event, data)
  window.cwreport?.report(event, data)
}

const getStepName = (step: number) =>
  STEP_TITLES[step as StepIndex] ?? STEP_TITLES[1]

const getCategoryLabels = () =>
  stores.OnboardingStore.selectedCategories
    .map((value) => CATEGORY_LABELS[value])
    .filter((label): label is string => Boolean(label))

const getRegionLabels = () =>
  stores.OnboardingStore.selectedRegions
    .map((value) => REGION_LABELS[value])
    .filter((label): label is string => Boolean(label))

const getSolutionLabels = () =>
  stores.OnboardingStore.selectedSolutions
    .map((item) => SOLUTION_LABELS[item.value])
    .filter((label): label is string => Boolean(label))

const getReasonLabels = () => {
  const onboarding = stores.OnboardingStore
  const idToValue = new Map<number, string>()
  for (const bucket of onboarding.reasonsLocales.en ?? []) {
    for (const reason of bucket.reasons) {
      idToValue.set(reason.id, reason.value)
    }
  }
  return onboarding.selectedReasons
    .map((id) => idToValue.get(id))
    .filter((value): value is string => Boolean(value))
}

export const reportView = () => {
  const step = stores.OnboardingStore.currentStep
  report("view_onboarding", { step_index: step, step_name: getStepName(step) })
}

export const reportSkip = () => {
  const step = stores.OnboardingStore.currentStep
  report("skip_onboarding", { step_index: step, step_name: getStepName(step) })
}

export const reportContinueStep1 = () => {
  report("continue_onboarding", {
    step_index: 1,
    step_name: getStepName(1),
    category: getCategoryLabels(),
    customer_country: getRegionLabels(),
  })
}

export const reportContinueStep2 = () => {
  const onboarding = stores.OnboardingStore
  report("continue_onboarding", {
    step_index: 2,
    step_name: getStepName(2),
    return_reasons: getReasonLabels(),
    return_window_days: onboarding.returnDays,
    return_window_start: RULE_TYPE_LABELS[onboarding.returnRuleType] ?? "",
    return_solutions: getSolutionLabels(),
  })
}

export const reportContinueStep3 = () => {
  const onboarding = stores.OnboardingStore
  report("continue_onboarding", {
    step_index: 3,
    step_name: getStepName(3),
    logo_uploaded: !!onboarding.logoUrl,
    theme_color_selected: onboarding.themeColor !== DEFAULT_THEME_COLOR,
  })
}

export const reportFinish = () => {
  report("finish_onboarding", { step_index: 4, step_name: getStepName(4) })
}
